import { Translatable } from './translatable'

interface AppErrorOptions extends ErrorOptions {
  uid?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Message = Translatable<any> | string

function normalizeUid(uid?: string): string | undefined {
  if (!uid) return
  return uid.replaceAll(/[<>]/g, '')
}

export class AppError extends Error {
  readonly messageWithoutUid: Message
  readonly uid?: string

  constructor(readonly code: string, message: Message, options?: AppErrorOptions) {
    super(AppError.getMessageWithUid(message, normalizeUid(options?.uid)), options)
    if (options?.uid) {
      this.uid = normalizeUid(options.uid)
      this.messageWithoutUid = message
    } else {
      const m = AppError.parseMessage(message)
      this.uid = m.uid
      this.messageWithoutUid = m.messageWithoutUid
    }
  }

  private static getMessageWithUid(message: Message, uid?: string): string {
    if (typeof message === 'string') {
      return uid ? `<${uid}> ${message}` : message
    } else {
      return uid ? `<${uid}> Untranslated message` : 'Untranslated message'
    }
  }

  private static parseMessage(message: Message): { uid?: string; messageWithoutUid: Message } {
    if (typeof message === 'string') {
      const m = message.match(/<([0-9a-z]{8})>/)
      if (!m || m.length === 0) return { messageWithoutUid: message }
      return {
        uid: m[1],
        messageWithoutUid: message.replace(m[0], '').trim(),
      }
    } else {
      return {
        messageWithoutUid: message,
      }
    }
  }
}

export function isAppError(e: unknown): e is AppError {
  return typeof e === 'object' && e instanceof AppError
}

export enum AppErrorCode {
  NOT_IMPLEMENTED = 'NOT_IMPLEMENTED',
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  OBJECT_NOT_FOUND = 'OBJECT_NOT_FOUND',
  OBJECT_ALREADY_EXISTS = 'OBJECT_ALREADY_EXISTS',
  AUTHORIZATION_NEEDED = 'AUTHORIZATION_NEEDED',
  ACCESS_DENIED = 'ACCESS_DENIED',
  INVALID_PARAMETER = 'INVALID_PARAMETER',
  INVALID_OPERATION = 'INVALID_OPERATION',
  NETWORK_ERROR = 'NETWORK_ERROR',
}

export function throwError(code: AppErrorCode, message: Message, options?: AppErrorOptions): never {
  throw new AppError(code, message, options)
}

export function throwAccessDenied(message: Message, options?: AppErrorOptions): never {
  throw new AppError(AppErrorCode.ACCESS_DENIED, message, options)
}

export function throwAuthorizationNeeded(message: Message, options?: AppErrorOptions): never {
  throw new AppError(AppErrorCode.AUTHORIZATION_NEEDED, message, options)
}

export function throwInternalError(message: Message, options?: AppErrorOptions): never {
  throwError(AppErrorCode.INTERNAL_ERROR, message, options)
}

export function throwObjectNotFound(message: Message, options?: AppErrorOptions): never {
  throwError(AppErrorCode.OBJECT_NOT_FOUND, message, options)
}

export function throwObjectAlreadyExists(message: Message, options?: AppErrorOptions): never {
  throwError(AppErrorCode.OBJECT_ALREADY_EXISTS, message, options)
}

export function throwInvalidParameter(message: Message, options?: AppErrorOptions): never {
  throwError(AppErrorCode.INVALID_PARAMETER, message, options)
}

export function throwInvalidOperation(message: Message, options?: AppErrorOptions): never {
  throwError(AppErrorCode.INVALID_OPERATION, message, options)
}

export function throwNotImplemented(message?: Message, options?: AppErrorOptions): never {
  if (typeof message === 'string') {
    throwError(AppErrorCode.NOT_IMPLEMENTED, `${message}: Not implemented`, options)
  } else {
    throwError(AppErrorCode.NOT_IMPLEMENTED, message ?? 'Not implemented', options)
  }
}
